/**
 * Landing Page Component
 * საწყისი გვერდი - აღწერა, ფასები, ავტორიზაცია
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@aiia/portal-core';
import { getPublicPlans } from '../features/api/subscription';
import type { Plan } from '../features/api/subscription';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { LogoIcon } from '../components/Logo';

// Social Provider Icons
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const GitHubIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

// @ts-expect-error Temporarily disabled — will re-enable when Apple login is configured
const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// @ts-expect-error Temporarily disabled — will re-enable when Microsoft login is configured
const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

// Social login providers
const socialProviders = [
  { id: 'google', name: 'Google', icon: GoogleIcon, hint: 'google' },
  { id: 'facebook', name: 'Facebook', icon: FacebookIcon, hint: 'facebook' },
  { id: 'github', name: 'GitHub', icon: GitHubIcon, hint: 'github' },
  // { id: 'apple', name: 'Apple', icon: AppleIcon, hint: 'apple' },
  // { id: 'microsoft', name: 'Microsoft', icon: MicrosoftIcon, hint: 'microsoft' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading, user, login } = useAuth();
  const lang = i18n.language || 'en';
  const [showAuthDropdown, setShowAuthDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Feature data
  const getFeatures = () => [
    { icon: '🤖', title: t('landing.feature1Title'), description: t('landing.feature1Desc') },
    { icon: '📊', title: t('landing.feature2Title'), description: t('landing.feature2Desc') },
    { icon: '🔗', title: t('landing.feature3Title'), description: t('landing.feature3Desc') },
    { icon: '🛡️', title: t('landing.feature4Title'), description: t('landing.feature4Desc') },
    { icon: '🌐', title: t('landing.feature5Title'), description: t('landing.feature5Desc') },
    { icon: '⚡', title: t('landing.feature6Title'), description: t('landing.feature6Desc') },
  ];

  // Fetch real pricing plans from API
  const { data: plans, isLoading: plansLoading, isError: plansError } = useQuery<Plan[]>({
    queryKey: ['publicPlans'],
    queryFn: getPublicPlans,
    staleTime: 5 * 60 * 1000,
  });

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle social login via Keycloak
  const handleSocialLogin = (providerHint: string) => {
    login({
      idpHint: providerHint,
      redirectUri: window.location.origin + '/' + lang + '/dashboard',
    });
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/">
              <LogoIcon size={108} />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {t('landing.features')}
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {t('landing.pricing')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {t('landing.aboutUs')}
              </button>
            </nav>

            {/* Language + Auth */}
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
            <div className="relative">
              {isLoading ? (
                <div className="w-24 h-10 bg-gray-200 rounded-lg animate-pulse" />
              ) : isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <span className="text-gray-700 font-medium hidden sm:block">
                    {user?.firstName || user?.email}
                  </span>
                  <button
                    onClick={() => navigate(`/${lang}/dashboard`)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <span>{t('landing.portal')}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowAuthDropdown(!showAuthDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <span>{t('landing.login')}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showAuthDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Auth Dropdown */}
                  {showAuthDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fadeIn">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm text-gray-500">{t('landing.loginWith')}</p>
                      </div>
                      <div className="py-2">
                        {socialProviders.map((provider) => (
                          <button
                            key={provider.id}
                            onClick={() => handleSocialLogin(provider.hint)}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors w-full text-left"
                          >
                            <provider.icon />
                            <span className="font-medium text-gray-700">{t('landing.loginWithProvider', { provider: provider.name })}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('landing.heroTitle1')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              {' '}{t('landing.heroTitle2')}
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            {t('landing.heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                if (isAuthenticated) {
                  navigate(`/${lang}/dashboard`);
                } else {
                  setShowAuthDropdown(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold text-lg shadow-lg hover:shadow-xl"
            >
              {isAuthenticated ? t('landing.enterPortal') : t('landing.startFree')}
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="px-8 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-semibold text-lg border border-gray-200"
            >
              {t('landing.learnMore')}
            </button>
          </div>

          {/* Hero Image/Demo */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-b from-blue-100 to-transparent rounded-3xl p-8">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      🤖
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3 max-w-md">
                      <p className="text-gray-700">{t('landing.demoGreeting')}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <div className="bg-blue-600 text-white rounded-2xl rounded-tr-none px-4 py-3 max-w-md">
                      <p>{t('landing.demoQuestion')}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      🤖
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3 max-w-md">
                      <p className="text-gray-700">
                        {t('landing.demoAnswer')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.featuresTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {getFeatures().map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all group"
              >
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.pricingTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.pricingSubtitle')}
            </p>
          </div>

          {plansLoading ? (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-8 border border-gray-200 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-6" />
                  <div className="h-10 bg-gray-200 rounded w-1/3 mb-6" />
                  <div className="space-y-3 mb-8">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="h-4 bg-gray-200 rounded w-full" />
                    ))}
                  </div>
                  <div className="h-12 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : plansError || !plans ? (
            <p className="text-gray-500 text-center">{t('landing.pricingSubtitle')}</p>
          ) : (
            <div className={`grid gap-8 max-w-5xl mx-auto ${plans.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
              {plans.map((plan) => {
                const highlighted = !!plan.recommended;
                const isEnterprise = plan.code === 'ENTERPRISE';
                const priceDisplay = isEnterprise
                  ? t('landing.customPricing')
                  : plan.monthlyPrice === 0
                    ? t('landing.free')
                    : String(plan.monthlyPrice);
                return (
                  <div
                    key={plan.code}
                    className={`bg-white rounded-2xl p-8 ${
                      highlighted
                        ? 'border-2 border-blue-600 shadow-xl scale-105'
                        : 'border border-gray-200'
                    }`}
                  >
                    {highlighted && (
                      <div className="text-center mb-4">
                        <span className="bg-blue-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                          {t('landing.popular')}
                        </span>
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    {plan.description && <p className="text-gray-600 mb-4">{plan.description}</p>}
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-gray-900">{priceDisplay}</span>
                      {!isEnterprise && plan.monthlyPrice > 0 && (
                        <>
                          <span className="text-xl text-gray-600">{plan.currency === 'GEL' ? '₾' : plan.currency}</span>
                          <span className="text-gray-600">/{t('landing.perMonth')}</span>
                        </>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features
                        .filter((feature) => {
                          const val = Number(feature.value);
                          const isBool = !feature.unlimited && (val === 0 || val === 1)
                            && !feature.code.startsWith('MAX_');
                          return !(isBool && val === 0);
                        })
                        .map((feature) => {
                          const val = Number(feature.value);
                          const isBooleanFeature = !feature.unlimited && (val === 0 || val === 1)
                            && !feature.code.startsWith('MAX_');
                          return (
                            <li key={feature.code} className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-gray-600">
                                {isBooleanFeature
                                  ? feature.name
                                  : `${feature.name}: ${feature.unlimited ? '∞' : feature.displayValue || String(feature.value)}`}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                    {isEnterprise ? (
                      <div className="text-center">
                        <button
                          disabled
                          className="w-full py-3 rounded-xl font-semibold bg-gray-100 text-gray-500 cursor-not-allowed"
                        >
                          {t('landing.contactUs')}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">info@ai-ia.dev</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          if (isAuthenticated) {
                            navigate(`/${lang}/subscription`);
                          } else {
                            setShowAuthDropdown(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                        className={`w-full py-3 rounded-xl font-semibold transition-colors ${
                          highlighted
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {t('landing.selectPlan')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* About/CTA Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-12 text-center text-white">
            <h2 className="text-4xl font-bold mb-4">{t('landing.ctaTitle')}</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t('landing.ctaSubtitle')}
            </p>
            <button
              onClick={() => {
                setShowAuthDropdown(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-semibold text-lg"
            >
              {t('landing.ctaCta')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <LogoIcon size={108} />
              </div>
              <p className="text-sm">
                {t('landing.footerTagline')}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerProduct')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">{t('landing.features')}</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">{t('landing.pricing')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerCompany')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${lang}/about`} className="hover:text-white transition-colors">{t('landing.footerAbout')}</Link></li>
                <li><Link to={`/${lang}/contact`} className="hover:text-white transition-colors">{t('landing.footerContact')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerLegal')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${lang}/privacy`} className="hover:text-white transition-colors">{t('landing.footerPrivacy')}</Link></li>
                <li><Link to={`/${lang}/terms`} className="hover:text-white transition-colors">{t('landing.footerTerms')}</Link></li>
                <li><Link to={`/${lang}/cookies`} className="hover:text-white transition-colors">{t('landing.footerCookies')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} AIIA. {t('public.allRightsReserved')}</p>
          </div>
        </div>
      </footer>

      {/* Click outside to close dropdown */}
      {showAuthDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowAuthDropdown(false)}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
